import { useState, type FormEvent } from 'react';
import type { CreateApiKeyDto } from '@yawi/types';
import { Button, Input, Select, Modal } from '@/core/ui/components';
import { useServices, useToast } from '@/app/providers';

interface ApiKeyFormModalProps {
  onClose: () => void;
  onSuccess: (key: string) => void;
}

const SCOPE_OPTIONS = [
  { value: 'read:tenants', label: 'Read Tenants' },
  { value: 'write:tenants', label: 'Write Tenants' },
  { value: 'read:users', label: 'Read Users' },
  { value: 'write:users', label: 'Write Users' },
  { value: 'read:billing', label: 'Read Billing' },
  { value: 'write:billing', label: 'Write Billing' },
  { value: 'admin', label: 'Full Admin Access' },
];

const EXPIRATION_OPTIONS = [
  { value: '', label: 'Never expires' },
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days' },
  { value: '180', label: '180 days' },
  { value: '365', label: '1 year' },
];

export function ApiKeyFormModal({ onClose, onSuccess }: ApiKeyFormModalProps) {
  const { apiKeyRepository } = useServices();
  const { showToast } = useToast();

  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<CreateApiKeyDto>({
    name: '',
    scopes: [],
    expiresInDays: undefined,
  });
  const [selectedScopes, setSelectedScopes] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (selectedScopes.length === 0) {
      newErrors.scopes = 'At least one scope is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);
    try {
      const response = await apiKeyRepository.create({
        ...formData,
        scopes: selectedScopes,
      });
      showToast('success', 'API key created successfully');
      onSuccess(response.key);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create API key';
      showToast('error', message);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleScope = (scope: string) => {
    setSelectedScopes((prev) =>
      prev.includes(scope)
        ? prev.filter((s) => s !== scope)
        : [...prev, scope]
    );
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Create API Key"
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} isLoading={isLoading}>
            Create
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        <Input
          label="Name"
          value={formData.name}
          onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
          error={errors.name}
          placeholder="My API Key"
        />

        <Input
          label="Tenant ID (optional)"
          value={formData.tenantId || ''}
          onChange={(e) => setFormData((prev) => ({ ...prev, tenantId: e.target.value || undefined }))}
          placeholder="Leave empty for global key"
        />

        <div>
          <label className="block text-sm font-medium text-theme-secondary mb-2">
            Scopes
          </label>
          <div className="space-y-2">
            {SCOPE_OPTIONS.map((option) => (
              <label
                key={option.value}
                className="flex items-center gap-3 p-3 rounded-lg border border-theme hover:bg-slate-50 dark:hover:bg-slate-800 cursor-pointer transition-colors"
              >
                <input
                  type="checkbox"
                  checked={selectedScopes.includes(option.value)}
                  onChange={() => toggleScope(option.value)}
                  className="w-4 h-4 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500"
                />
                <span className="text-sm text-theme">{option.label}</span>
              </label>
            ))}
          </div>
          {errors.scopes && (
            <p className="mt-1.5 text-sm text-red-500">{errors.scopes}</p>
          )}
        </div>

        <Select
          label="Expiration"
          value={formData.expiresInDays?.toString() || ''}
          onChange={(e) =>
            setFormData((prev) => ({
              ...prev,
              expiresInDays: e.target.value ? parseInt(e.target.value) : undefined,
            }))
          }
          options={EXPIRATION_OPTIONS}
        />
      </form>
    </Modal>
  );
}
